import type { OAuthConfig } from '@goodsteward/gs-auth-spa';

// One OAuth config drives both dev and prod. The local stub IdP at
// http://localhost:9000 speaks the same /oauth2 dialect Cognito Hosted
// UI does — only the host changes between environments.
//
// Dev defaults are inlined so a fresh checkout works against the stub
// (`npm run dev:stub`) without anyone touching .env. Test/prod set
// VITE_COGNITO_* explicitly via the deploy environment.
//
// Impersonation (per ADR 0007) keeps a separate config because in prod
// it uses a different Cognito app client with a 1-hour token TTL. In
// dev that's just the same stub IdP with a different clientId, but
// the env-driven structure stays so the prod path is exercised.

export function getCognitoConfig(): OAuthConfig {
    const env = import.meta.env;
    const origin = typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost:5173';
    return {
        domain:            (env.VITE_COGNITO_DOMAIN        as string) || 'http://localhost:9000',
        clientId:          (env.VITE_COGNITO_CLIENT_ID     as string) || 'gs-stub',
        redirectUri:       (env.VITE_COGNITO_REDIRECT_URI  as string) || `${origin}/auth/callback`,
        logoutRedirectUri: (env.VITE_COGNITO_LOGOUT_URI    as string) || `${origin}/auth/logout`,
    };
}

/**
 * Per ADR 0007 the GS-staff impersonation flow uses a dedicated
 * Cognito app client (different client_id, different redirect URI,
 * 1h token TTL). Same Hosted UI domain, same JWKS — only the OAuth
 * config differs.
 *
 * Used by /impersonate-start (kicks off the OAuth dance) and
 * /impersonate-callback (exchanges the code). Both pages build the
 * config inside their handler so a build with the impersonation env
 * vars unset doesn't crash on import.
 */
export function getCognitoImpersonationConfig(): OAuthConfig {
    const env = import.meta.env;
    const required = (k: keyof typeof env, label: string) => {
        const v = env[k];
        if (!v) {
            throw new Error(
                `${label} is required for impersonation (set ${String(k)} in your .env).`,
            );
        }
        return v as string;
    };
    return {
        // Same Hosted UI domain — one Cognito user pool.
        domain:            required('VITE_COGNITO_DOMAIN',                       'Cognito Hosted UI domain'),
        clientId:          required('VITE_COGNITO_IMPERSONATION_CLIENT_ID',      'Cognito impersonation app client ID'),
        redirectUri:       required('VITE_COGNITO_IMPERSONATION_REDIRECT_URI',   'Cognito impersonation callback URL'),
        // logoutRedirectUri is unused for impersonation (closing the
        // tab ends the session), but OAuthConfig requires it. Reuse
        // the regular client's logout URI for type compatibility.
        logoutRedirectUri: required('VITE_COGNITO_LOGOUT_URI',                   'Cognito logout return URL'),
    };
}

/**
 * Apex domain (with leading dot) the SPA mirrors the access token onto
 * so the support-site Lambda@Edge gate sees it on cross-subdomain
 * navigation. Empty string in local dev — the cookie helper no-ops.
 */
export function getPlatformCookieDomain(): string {
    return (import.meta.env.VITE_PLATFORM_COOKIE_DOMAIN as string | undefined) ?? '';
}
